import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { 
  Pencil, 
  Type, 
  ArrowRight, 
  Square, 
  Circle,
  Eraser,
  Undo2,
  Save,
  X,
  Palette
} from 'lucide-react';

const TOOLS = {
  pencil: { icon: Pencil, name: 'Pędzel' },
  text: { icon: Type, name: 'Tekst' },
  arrow: { icon: ArrowRight, name: 'Strzałka' },
  rectangle: { icon: Square, name: 'Prostokąt' },
  circle: { icon: Circle, name: 'Okrąg' },
  eraser: { icon: Eraser, name: 'Gumka' }
};

const COLORS = [
  '#EF4444', // red
  '#F97316', // orange
  '#EAB308', // yellow
  '#22C55E', // green
  '#3B82F6', // blue
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#FFFFFF', // white
  '#000000', // black
];

export default function PhotoAnnotationCanvas({ 
  imageUrl, 
  existingAnnotations = [],
  onSave, 
  onClose,
  language = 'pl'
}) {
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('pencil');
  const [color, setColor] = useState('#EF4444');
  const [lineWidth, setLineWidth] = useState(3);
  const [history, setHistory] = useState([]);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = canvas.parentElement;
    
    // Load image and set canvas size
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const aspectRatio = img.width / img.height;
      const maxWidth = container.clientWidth - 40;
      const maxHeight = window.innerHeight - 200;
      
      let width = maxWidth;
      let height = width / aspectRatio;
      
      if (height > maxHeight) {
        height = maxHeight;
        width = height * aspectRatio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const context = canvas.getContext('2d');
      context.drawImage(img, 0, 0, width, height);
      context.lineCap = 'round';
      context.lineJoin = 'round';
      contextRef.current = context;
      
      // Save initial state
      saveToHistory();
      
      // Draw existing annotations
      if (existingAnnotations.length > 0) {
        drawAnnotations(existingAnnotations);
      }
    };
    img.src = imageUrl;
  }, [imageUrl, existingAnnotations]);

  const saveToHistory = () => {
    const canvas = canvasRef.current;
    setHistory(prev => [...prev, canvas.toDataURL()]);
  };

  const drawAnnotations = (annotations) => {
    const ctx = contextRef.current;
    annotations.forEach(ann => {
      ctx.strokeStyle = ann.color;
      ctx.lineWidth = ann.lineWidth;
      
      if (ann.type === 'path') {
        ctx.beginPath();
        ann.points.forEach((point, i) => {
          if (i === 0) ctx.moveTo(point.x, point.y);
          else ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();
      } else if (ann.type === 'text') {
        ctx.fillStyle = ann.color;
        ctx.font = `${ann.fontSize || 16}px Arial`;
        ctx.fillText(ann.text, ann.x, ann.y);
      }
    });
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const pos = getCoordinates(e);
    
    if (tool === 'text') {
      setTextPosition(pos);
      return;
    }
    
    setStartPos(pos);
    setIsDrawing(true);
    
    const ctx = contextRef.current;
    ctx.strokeStyle = tool === 'eraser' ? '#FFFFFF' : color;
    ctx.lineWidth = tool === 'eraser' ? lineWidth * 3 : lineWidth;
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const pos = getCoordinates(e);
    const ctx = contextRef.current;
    
    if (tool === 'pencil' || tool === 'eraser') {
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
  };

  const stopDrawing = (e) => {
    if (!isDrawing) return;
    e?.preventDefault();
    
    const ctx = contextRef.current;
    const pos = e ? getCoordinates(e) : startPos;
    
    if (tool === 'arrow') {
      drawArrow(ctx, startPos.x, startPos.y, pos.x, pos.y);
    } else if (tool === 'rectangle') {
      ctx.strokeRect(startPos.x, startPos.y, pos.x - startPos.x, pos.y - startPos.y);
    } else if (tool === 'circle') {
      const radius = Math.sqrt(Math.pow(pos.x - startPos.x, 2) + Math.pow(pos.y - startPos.y, 2));
      ctx.beginPath();
      ctx.arc(startPos.x, startPos.y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    ctx.closePath();
    setIsDrawing(false);
    saveToHistory();
  };

  const drawArrow = (ctx, fromX, fromY, toX, toY) => {
    const headLength = 15;
    const angle = Math.atan2(toY - fromY, toX - fromX);
    
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.lineTo(toX, toY);
    ctx.lineTo(toX - headLength * Math.cos(angle - Math.PI / 6), toY - headLength * Math.sin(angle - Math.PI / 6));
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - headLength * Math.cos(angle + Math.PI / 6), toY - headLength * Math.sin(angle + Math.PI / 6));
    ctx.stroke();
  };

  const addText = () => {
    if (!textInput || !textPosition) return;
    
    const ctx = contextRef.current;
    ctx.fillStyle = color;
    ctx.font = `${lineWidth * 6}px Arial`;
    ctx.fillText(textInput, textPosition.x, textPosition.y);
    
    setTextInput('');
    setTextPosition(null);
    saveToHistory();
  };

  const undo = () => {
    if (history.length <= 1) return;
    
    const newHistory = [...history];
    newHistory.pop();
    setHistory(newHistory);
    
    const img = new Image();
    img.onload = () => {
      const ctx = contextRef.current;
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = newHistory[newHistory.length - 1];
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave?.(dataUrl);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 bg-card border-b border-border">
        <div className="flex items-center gap-2">
          {/* Tools */}
          {Object.entries(TOOLS).map(([key, { icon: Icon, name }]) => (
            <Button
              key={key}
              variant={tool === key ? 'default' : 'outline'}
              size="icon"
              onClick={() => setTool(key)}
              title={name}
            >
              <Icon className="w-4 h-4" />
            </Button>
          ))}
          
          <div className="w-px h-8 bg-border mx-2" />
          
          {/* Colors */}
          <div className="flex gap-1">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-6 h-6 rounded-sm border-2 ${
                  color === c ? 'border-primary scale-110' : 'border-transparent'
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          
          <div className="w-px h-8 bg-border mx-2" />
          
          {/* Line Width */}
          <div className="flex items-center gap-2 w-32">
            <span className="text-xs text-muted-foreground">{lineWidth}px</span>
            <Slider
              value={[lineWidth]}
              onValueChange={([v]) => setLineWidth(v)}
              min={1}
              max={10}
              step={1}
            />
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={undo}>
            <Undo2 className="w-4 h-4 mr-2" />
            {language === 'pl' ? 'Cofnij' : 'Undo'}
          </Button>
          <Button variant="default" size="sm" onClick={handleSave}>
            <Save className="w-4 h-4 mr-2" />
            {language === 'pl' ? 'Zapisz' : 'Save'}
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>
      
      {/* Canvas */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          className="max-w-full max-h-full cursor-crosshair border border-border"
        />
      </div>
      
      {/* Text Input Modal */}
      {textPosition && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="bg-card p-4 rounded-sm space-y-4 max-w-md w-full mx-4">
            <h3 className="font-semibold">{language === 'pl' ? 'Dodaj tekst' : 'Add Text'}</h3>
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder={language === 'pl' ? 'Wpisz tekst...' : 'Enter text...'}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setTextPosition(null)}>
                {language === 'pl' ? 'Anuluj' : 'Cancel'}
              </Button>
              <Button onClick={addText}>
                {language === 'pl' ? 'Dodaj' : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
